import { api } from '@/http/constants';
import { getRequestHeaders } from '@/http/fetchWrapper';
import { useStoreLoginSession } from '@/hooks/useStoreLoginSession';
import type { MfaConfirmRequest, MfaConfirmResponse } from '@eye-able/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mfaErrorFromStatus } from './mfaError';

export interface MfaSetupConfirmation {
  /** Shown once, with the user's first method only. */
  backupCodes: string[] | null;
  /**
   * Mid-login the response also carries the session. Opening it redirects
   * into the app, so the caller does that once the backup codes were seen.
   */
  openSession: (() => Promise<void>) | null;
}

/**
 * Proves the user holds the method they set up, which turns it on. A raw
 * fetch, since mid-login the session token may arrive as a header. Whether a
 * session came along shows in the body: a login answer carries `tokenExpires`.
 */
export function useConfirmMfaSetup() {
  const storeLoginSession = useStoreLoginSession();
  const queryClient = useQueryClient();

  return useMutation({
    // The backup codes must not linger in the cache.
    gcTime: 0,
    mutationFn: async (
      body: MfaConfirmRequest,
    ): Promise<MfaSetupConfirmation> => {
      const response = await fetch(api.mfaConfirm, {
        mode: 'cors',
        method: 'POST',
        cache: 'no-cache',
        credentials: import.meta.env.PROD ? 'include' : 'omit',
        headers: getRequestHeaders(api.mfaConfirm),
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw mfaErrorFromStatus(response.status);
      }

      const json = (await response.clone().json()) as MfaConfirmResponse & {
        tokenExpires?: number;
      };
      const { backupCodes } = json;
      if (json.tokenExpires === undefined) {
        await queryClient.invalidateQueries({ queryKey: [api.mfa] });
        return { backupCodes, openSession: null };
      }
      return {
        backupCodes,
        // A clone each time: a retry after a later step failed must not
        // find the body already read.
        openSession: async () => {
          await storeLoginSession(response.clone());
        },
      };
    },
  });
}
